<template>
  <el-form
    ref="form"
    class="form-container"
    :class="[requiredAsteriskPosition === 'after' ? 'asterisk-after' : 'asterisk-before', labelBold && 'label-bold', labelPosition && `label-position-${labelPosition}`]"
    :label-position="labelPosition"
    :label-width="labelWidth"
    :style="formStyle"
    :hide-required-asterisk="hideRequiredAsterisk"
    :model="model"
    :rules="formRules"
    :validate-on-rule-change="validateOnRuleChange"
    :disabled="disabled"
    @submit.native.prevent
  >
    <template v-for="(item, key) in wrappedFormLines">
      <slot :name="item.lineSlot" :line="{ name: key, line: item, componentSelf }">
        <el-form-item
          v-if="!item.hidden"
          :key="key"
          :class="[item.class, item.multiple && 'multiple-line']"
          :label="item.label || $t(item.labelI18nKey)"
          :prop="item.hiddenPropName ? null : key"
          :required="item.required"
        >
          <template #label>
            <div class="label-inner">
              <slot :name="item.labelSlot" :lineName="key" :line="item">{{ item.label }}</slot>
            </div>
          </template>
          <slot :name="item.contentSlot" :lineName="key" :line="item">
            <template v-if="item.multiple">
              <template v-for="(multipleItem, multipleName) in item.multiple">
                <el-form-item
                  v-if="!item.hidden"
                  class="multiple-line-item"
                  :key="multipleName"
                  :class="multipleItem.class"
                  :label="multipleItem.label"
                  :prop="multipleItem.hiddenPropName ? null : multipleName"
                  :required="multipleItem.required"
                >
                  <slot v-if="multipleItem.contentSlot" :name="multipleItem.contentSlot" :lineName="key" :line="item" :multipleName="multipleName" :multipleItem="multipleItem"></slot>
                  <custom-remote-el-select
                    v-else-if="multipleItem.elType === 'el-select'"
                    v-model="model[multipleName]"
                    v-bind="multipleItem.props"
                    :name="multipleName"
                    :disabled="multipleItem.disabled"
                    :placeholder="multipleItem.placeholder"
                    :loading="multipleItem.option.loading"
                    :remote-method="multipleItem.remoteMethod"
                    @visible-change="handleSelectVisibleChange($event, multipleName, multipleItem, key, item)"
                    @change="handleSelectChange($event, multipleName, multipleItem, key, item)"
                  >
                    <template v-if="multipleItem.option.grouped">
                      <el-option-group
                        v-for="(group, groupIndex) in multipleItem.option.data"
                        :key="groupIndex"
                        :label="multipleItem.option.groupLabelKey ? group[multipleItem.option.groupLabelKey] : group.label"
                      >
                        <el-option
                          v-for="(option, index) in (multipleItem.option.groupOptionsDataKey ? group[multipleItem.option.groupOptionsDataKey] : group.options)"
                          :key="index"
                          :label="multipleItem.option.labelKey ? option[multipleItem.option.labelKey] : option.label"
                          :value="multipleItem.option.valueKey ? option[multipleItem.option.valueKey] : option.value"
                          :disabled="option.disabled"
                        />
                      </el-option-group>
                    </template>
                    <template v-else>
                      <el-option
                        v-for="(option, index) in multipleItem.option.data"
                        :key="index"
                        :label="multipleItem.option.labelKey ? option[multipleItem.option.labelKey] : option.label"
                        :value="multipleItem.option.valueKey ? option[multipleItem.option.valueKey] : option.value"
                        :disabled="option.disabled"
                      />
                    </template>
                  </custom-remote-el-select>
                  <el-checkbox
                    v-else-if="multipleItem.elType === 'el-checkbox'"
                    v-model="model[multipleName]"
                    v-bind="multipleItem.props"
                    :name="multipleName"
                    :disabled="multipleItem.disabled"
                  >
                    {{ multipleItem.checkboxLabel }}
                  </el-checkbox>
                  <el-radio-group
                    v-else-if="multipleItem.elType === 'el-radio-group'"
                    v-model="model[multipleName]"
                    v-bind="multipleItem.props"
                    :disabled="multipleItem.disabled"
                    @change="handleRadioGroupChange($event, multipleItem, item)"
                  >
                    <el-radio
                      v-for="(option, index) in multipleItem.option.data"
                      :key="index"
                      :name="multipleName"
                      :label="multipleItem.option.valueKey ? option[multipleItem.option.valueKey] : option.value"
                    >
                      <slot
                        :name="multipleItem.option.slotName"
                        :lineName="key"
                        :line="item"
                        :multipleName="multipleName"
                        :multipleItem="multipleItem"
                        :option="option"
                        :optionIndex="index"
                        :label="multipleItem.option.labelKey ? option[multipleItem.option.labelKey] : option.label"
                        :value="multipleItem.option.valueKey ? option[multipleItem.option.valueKey] : option.value"
                      >
                        {{ multipleItem.option.labelKey ? option[multipleItem.option.labelKey] : option.label }}
                      </slot>
                    </el-radio>
                  </el-radio-group>
                  <el-date-picker
                    v-else-if="multipleItem.elType === 'el-date-picker'"
                    v-model="model[multipleName]"
                    v-bind="multipleItem.props"
                    :name="multipleName"
                    :disabled="multipleItem.disabled"
                    :placeholder="multipleItem.placeholder"
                  ></el-date-picker>
                  <el-input
                    v-else-if="multipleItem.elType === 'el-input' || !multipleItem.elType"
                    v-model="model[multipleName]"
                    v-bind="multipleItem.props"
                    :name="multipleName"
                    :disabled="multipleItem.disabled"
                    :placeholder="multipleItem.placeholder"
                  />
                  <component
                    v-else
                    v-model="model[multipleName]"
                    v-bind="multipleItem.props"
                    :is="multipleItem.elType"
                    :name="multipleName"
                    :disabled="multipleItem.disabled"
                  ></component>
                </el-form-item>
              </template>
            </template>
            <custom-remote-el-select
              v-else-if="item.elType === 'el-select'"
              v-model="model[key]"
              v-bind="item.props"
              :name="key"
              :disabled="item.disabled"
              :placeholder="item.placeholder"
              :loading="item.option.loading"
              :remote-method="item.remoteMethod"
              :ref="key"
              @visible-change="handleSelectVisibleChange($event, key, item)"
              @change="handleSelectChange($event, key, item)"
            >
              <template v-if="item.option.grouped">
                <el-option-group
                  v-for="(group, groupIndex) in item.option.data"
                  :key="groupIndex"
                  :label="item.option.groupLabelKey ? group[item.option.groupLabelKey] : group.label"
                >
                  <el-option
                    v-for="(option, index) in (item.option.groupOptionsDataKey ? group[item.option.groupOptionsDataKey] : group.options)"
                    :key="index"
                    :label="item.option.labelKey ? option[item.option.labelKey] : option.label"
                    :value="item.option.valueKey ? option[item.option.valueKey] : option.value"
                  />
                </el-option-group>
              </template>
              <template v-else>
                <el-option
                  v-for="(option, index) in item.option.data"
                  :key="index"
                  :label="item.option.labelKey ? option[item.option.labelKey] : option.label"
                  :value="item.option.valueKey ? option[item.option.valueKey] : option.value"
                  :disabled="option.disabled"
                />
              </template>
            </custom-remote-el-select>
            <el-checkbox
              v-else-if="item.elType === 'el-checkbox'"
              v-model="model[key]"
              v-bind="item.props"
              :name="key"
              :disabled="item.disabled"
            >
              {{ item.checkboxLabel }}
            </el-checkbox>
            <el-checkbox-group
              v-else-if="item.elType === 'el-checkbox-group'"
              v-model="model[key]"
              v-bind="item.props"
              :name="key"
              :disabled="item.disabled"
            >
              <el-checkbox
                v-for="(option, index) in item.option.data"
                :key="index"
                :name="key"
                :label="item.option.valueKey ? option[item.option.valueKey] : option.value"
              >
                {{ item.option.labelKey ? option[item.option.labelKey] : option.label }}
              </el-checkbox>
            </el-checkbox-group>
            <el-radio-group
              v-else-if="item.elType === 'el-radio-group'"
              v-model="model[key]"
              v-bind="item.props"
              :disabled="item.disabled"
              @change="handleRadioGroupChange($event, item)"
            >
              <el-radio
                v-for="(option, index) in item.option.data"
                :key="index"
                :name="key"
                :label="item.option.valueKey ? option[item.option.valueKey] : option.value"
              >
                <slot
                  :name="item.option.slotName"
                  :lineName="key"
                  :line="item"
                  :option="option"
                  :optionIndex="index"
                  :label="item.option.labelKey ? option[item.option.labelKey] : option.label"
                  :value="item.option.valueKey ? option[item.option.valueKey] : option.value"
                >
                  {{ item.option.labelKey ? option[item.option.labelKey] : option.label }}
                </slot>
              </el-radio>
            </el-radio-group>
            <el-date-picker
              v-else-if="item.elType === 'el-date-picker'"
              v-model="model[key]"
              v-bind="item.props"
              :name="key"
              :disabled="item.disabled"
              :placeholder="item.placeholder"
              :default-value="item.defaultValue"
            ></el-date-picker>
            <el-input
              v-else-if="item.elType === 'el-input' || !item.elType"
              v-model="model[key]"
              v-bind="item.props"
              v-on="item.events"
              :name="key"
              :disabled="item.disabled"
              :placeholder="item.placeholder"
            />
            <component
              v-else
              v-model="model[key]"
              v-bind="item.props"
              v-on="item.events"
              :is="item.elType"
              :name="key"
              :disabled="item.disabled"
            ></component>
          </slot>
          <slot :name="item.contentExtraSlot" :lineName="key" :line="item"></slot>
        </el-form-item>
      </slot>
    </template>
  </el-form>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'wrapped-form',
  props: {
    labelPosition: {
      type: String,
      default: 'right'
    },
    labelWidth: {
      default: 'auto'
    },
    labelBold: {
      type: Boolean,
      default: false
    },
    formStyle: {
      default: null
    },
    model: {
      required: true
    },
    formLines: {
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    requiredAsteriskPosition: {
      type: String,
      default: 'after'
    },
    hideRequiredAsterisk: {
      type: Boolean,
      default: false
    },
    validateOnRuleChange: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      componentSelf: this,
      willDestroy: false,
      wrappedFormLines: {},
      selectLines: {},
      clearOnChangeLines: {}
    }
  },
  computed: {
    formRules () {
      const rules = {}
      _.forEach(this.formLines, (item, key) => {
        if (!item.hidden) {
          if (item.rules) {
            rules[key] = item.rules
          }
          _.forEach(item.multiple, (multipleItem, multipleName) => {
            if (!multipleItem.hidden && multipleItem.rules) {
              rules[multipleName] = multipleItem.rules
            }
          })
        }
      })
      return rules
    },
    formLinesWatcher () {
      return this.formLines
    }
  },
  created () {

  },
  methods: {
    async wrapFormLines () {
      const lines = {}
      const promiseList = []
      _.forEach(this.formLines, (line, name) => {
        const elType = line.elType
        if (elType === 'el-select') {
          line = this.wrapSelectLine(name, line, null, null, promiseList)
        }
        if (line.multiple) {
          const multipleLines = {}
          _.forEach(line.multiple, (multipleItem, multipleName) => {
            if (multipleItem.elType === 'el-select') {
              multipleItem = this.wrapSelectLine(multipleName, multipleItem, name, line, promiseList)
            }
            multipleLines[multipleName] = multipleItem
          })
          line.multiple = multipleLines
        }
        lines[name] = line
      })
      await Promise.all(promiseList)
      this.wrappedFormLines = lines
      await this.$nextTick()
      await this.clearValidate()
    },
    wrapSelectLine (selectName, selectLine, lineName, line, promiseList) {
      const props = {}
      const option = selectLine.option
      option.data = option.data || []
      option.loadedData = option.data
      option.loading = option.loading || false
      option.hasLoad = option.hasLoad || false
      option.hasFilter = option.hasFilter || false
      if (option.delayLoadData) {
        props.remote = true
        if (!option.hasLoad) {
          option.data = []
          option.loading = false
          option.hasLoad = false
          if (this.model[selectName] !== null && this.model[selectName] !== undefined && !selectLine.hidden) {
            option.loading = true
            Promise.resolve(option.delayLoadData(selectName, selectLine, lineName, line)).then((data) => {
              option.hasLoad = true
              option.data = data
              option.loadedData = data
            }).catch((e) => {
              option.hasLoad = false
              // eslint-disable-next-line no-console
              console.error(e)
              return e
            }).finally(() => {
              option.loading = false
            })
          }
        }
        this.$watch(`model.${selectName}`, (newVal, oldVal) => {
          if (newVal === oldVal || selectLine.hidden) {
            return
          }
          if (selectLine.props.multiple && (newVal || []).length === 0 && (oldVal || []).length === 0) {
            return
          }
          let reloadOption = false
          if (option.reloadIfUnmatched) {
            let query = null
            if (selectLine.props['value-key'] || selectLine.props.valueKey) {
              query = newVal
            } else {
              query = { [option.valueKey || 'value']: newVal }
            }
            reloadOption = _.findIndex(option.data, query) < 0
          }
          if ((reloadOption || (!option.loading && !option.hasLoad)) && newVal !== null && newVal !== undefined) {
            option.loading = true
            Promise.resolve(option.delayLoadData(selectName, selectLine, lineName, line)).then((data) => {
              option.hasLoad = true
              option.data = data
              option.loadedData = data
            }).catch((e) => {
              option.hasLoad = false
              // eslint-disable-next-line no-console
              console.error(e)
              return e
            }).finally(() => {
              option.loading = false
            })
          }
        })
      } else {
        option.hasLoad = true
      }
      if (selectLine.filterMethod) {
        props.filterable = true
        props.remote = true
        const filterMethod = selectLine.filterMethod
        selectLine.remoteMethod = _.debounce((keywords) => {
          if (option.loading || !option.hasLoad) {
            return
          }
          option.loading = true
          option.hasFilter = true
          Promise.resolve(filterMethod(keywords, option.loadedData)).then((filterResult) => {
            option.data = filterResult
          }).finally(() => {
            option.loading = false
          })
        }, selectLine.props?.debounce || 0)
      }
      if (selectLine.props) {
        _.merge(selectLine.props, props)
      } else {
        selectLine.props = props
      }

      this.$set(this.selectLines, selectName, selectLine)
      return selectLine
    },
    handleClearOnChange (changedLineName, changedLine, parentLineName, parentLine) {
      _.forEach(changedLine.clearOnChange, (lineName) => {
        let line = null
        if (parentLine && parentLine.multiple) {
          line = parentLine.multiple[lineName]
        }
        if (!line) {
          line = this.wrappedFormLines[lineName]
        }
        if (!line) {
          return
        }
        if (line.multiple) {
          _.forEach(line.multiple, (multipleItem, multipleName) => {
            if (multipleItem.elType === 'el-select') {
              const option = multipleItem.option
              option.hasFilter = false
              if (option.delayLoadData) {
                option.data = []
                option.hasLoad = false
                option.loading = false
              }
            }
            _.set(this.model, multipleName, null)
          })
        } else {
          if (line.elType === 'el-select') {
            const option = line.option
            option.hasFilter = false
            if (option.delayLoadData) {
              option.data = []
              option.hasLoad = false
              option.loading = false
            }
          }
          _.set(this.model, lineName, null)
        }
      })
    },
    handleSelectVisibleChange (visible, selectName, selectItem, lineName, line) {
      const option = selectItem.option
      if (visible && option.delayLoadData) {
        if (option.disableCacheLoadData) {
          option.hasLoad = false
        }
        if (!option.hasLoad && !option.loading && !selectItem.hidden) {
          option.loading = true
          Promise.resolve(option.delayLoadData(selectName, selectItem, lineName, line)).then((data) => {
            option.data = data
            option.loadedData = data
            option.hasLoad = true
          }).catch((e) => {
            option.hasLoad = false
            // eslint-disable-next-line no-console
            console.error(e)
            return e
          }).finally(() => {
            option.loading = false
          })
        }
      }
      if (visible && selectItem.loadMore) {
        this.$nextTick(() => {
          if (this.$refs[selectName]) {
            const elSelect = this.$refs[selectName][0]
            const el = elSelect?.popperElm.querySelector('.el-select-dropdown__wrap')
            if (el && !el.hasAttribute('filter-scroll-listened')) {
              el.setAttribute('filter-scroll-listened', true)
              el.addEventListener('scroll', () => {
                const condition = el.scrollHeight - el.scrollTop - 20 <= el.clientHeight
                if (condition) {
                  selectItem.loadMore(selectItem)
                }
              })
            }
          }
        })
      }
      if (!visible && option.hasFilter) {
        this.$nextTick(() => {
          option.data = option.loadedData
          option.hasFilter = false
        })
      }
      if (!selectItem.events) {
        return
      }
      const handler = selectItem.events['visible-change']
      if (handler) {
        handler(visible, selectName, selectItem, lineName, line)
      }
    },
    handleSelectChange (selected, selectName, selectItem, lineName, line) {
      if (selectItem.clearOnChange) {
        this.handleClearOnChange(selectName, selectItem, lineName, line)
      }
      if (!selectItem.events) {
        return
      }
      const handler = selectItem.events.change
      if (handler) {
        handler(selected, selectName, selectItem, lineName, line)
      }
    },
    handleRadioGroupChange (checkedValue, lineItem) {
      if (!lineItem.events) {
        return
      }
      const handler = lineItem.events.change
      if (handler) {
        handler(checkedValue, lineItem)
      }
    },
    async submit () {
      let result = false
      const validatePromise = new Promise((resolve) => {
        this.$refs.form.validate((isSuccess, failObject) => {
          if (!isSuccess) {
            this.$nextTick(() => {
              this.$el.querySelector('.el-form-item.is-error').scrollIntoView({ block: 'center', behavior: 'smooth' })
            })
          }
          resolve(isSuccess)
        })
      })
      result = await validatePromise
      return result
    },
    async clearValidate (props) {
      if (this.willDestroy) {
        return
      }
      let form = this.$refs.form
      if (!form) {
        await this.$nextTick()
        form = this.$refs.form
      }
      return form.clearValidate(props)
    },
    async validateField (props, callback) {
      let form = this.$refs.form
      if (!form) {
        await this.$nextTick()
        form = this.$refs.form
      }
      return await form.validateField(props, callback)
    }
  },
  beforeDestroy () {
    this.willDestroy = true
  },
  watch: {
    formLinesWatcher: {
      handler () { this.wrapFormLines() },
      immediate: true
    },
    formRules () {
      this.$nextTick(() => this.clearValidate())
    }
  }
}
</script>

<style lang="scss" scoped>
.form-container.el-form {
  &.asterisk-after {
    ::v-deep {
      .el-form-item {
        &:not(.is-no-asterisk) {
          .el-form-item__label {
            align-items: flex-start;
            &::after {
              content: '*';
              margin: 9px 0 0 3px;
              color: #f56c6c;
              line-height: 1;
              visibility: hidden;
            }
          }
        }
        &.is-required:not(.is-no-asterisk) {
          .el-form-item__label {
            &::before {
              display: none;
            }

            &::after {
              visibility: unset;
            }
          }
        }
      }
    }
  }

  &.el-form--label-top {
    ::v-deep {
      .el-form-item {
        .el-form-item__label {
          padding-bottom: 0;

          .label-inner {
            display: inline-block;
          }
        }
      }
    }
  }

  &:not(.el-form--label-top) {
    ::v-deep {
      .el-form-item {
        .el-form-item__label {
          display: flex;
          min-height: 32px;
          padding-right: 20px;
          align-items: flex-start;

          .label-inner {
            margin-top: 4px;
            line-height: normal;
          }
        }
      }
    }
  }

  &.label-position-left {
    ::v-deep {
      .el-form-item {
        .el-form-item__label {
          justify-content: flex-start;
        }
      }
    }
  }

  &.label-position-right {
    ::v-deep {
      .el-form-item {
        .el-form-item__label {
          justify-content: flex-end;
        }
      }
    }
  }

  &.label-bold {
    ::v-deep {
      .el-form-item__label {
        font-weight: bold;
      }
    }
  }

  .multiple-line {
    .multiple-line-item {
      display: inline-block;
      width: 50%;

      &:not(:first-child) {
        margin-left: -1px;
      }
    }
  }

  ::v-deep {
    .el-form-item {
      &.is-error {
        .el-input {
          .el-input__inner {
            border-color: #f56c6c;

            &:focus {
              border-color: #f56c6c;
            }

            &:hover {
              border-color: #f56c6c;
            }
          }
        }
      }
    }

    .el-form-item__label {
      line-height: 32px;
    }

    .el-form-item__content {
      line-height: 32px;
    }

    .el-select {
      width: 100%;

      .el-input {
        .el-input__icon {
          line-height: inherit;
        }

        &.is-focus {
          .el-input__inner {
            border-color: #409EFF;
          }
        }
      }

      .el-select__input {
        margin-left: 5px;
      }
    }

    .el-input {
      .el-input-group__prepend {
        border-radius: 0;
        padding: 0 10px;
      }

      .el-input__inner {
        height: 32px;
        line-height: 32px;
        padding: 0 5px;
        border-radius: 0;
        color: #232323;

        &:focus {
          border-color: #b2b2b2;
        }

        &:hover {
          border-color: #C0C4CC;
        }
      }

      &.el-input--prefix {
        .el-input__inner {
          padding-left: 30px;
        }

        .el-input__prefix {
          .el-input__icon {
            line-height: inherit;
          }
        }
      }

      &.el-input--suffix {
        .el-input__suffix {
          .el-input__icon {
            line-height: inherit;
          }
        }
      }

      &.password-input {
        .el-input__inner {
          padding-right: 50px;
        }

        .el-input__suffix {
          right: 20px;
        }
      }

      &.is-disabled  {
        .el-input__inner {
          background-color: #F5F7FA;
          border-color: #E4E7ED;
          color: #C0C4CC;
          cursor: not-allowed;
        }
      }
    }

    .el-autocomplete {
      width: 100%;
    }

    .el-textarea {
      word-break: break-word;

      .el-textarea__inner {
        padding: 5px;
        border-radius: 0;
      }
    }

    .el-date-editor {
      width: 100%;
    }
  }
}
</style>
